/**
 * Historical demand per section time slot, used to size the SL
 * gate's `slSlotCaps` (see `sectionSignupV2SlGate.ts`).
 *
 * Source: `course/{cip4,cip5,cip6}/sectionSignups/main`, adult pool,
 * picks of `"none"` excluded. Each value is the equal-weight mean
 * over the three runs of that slot's fraction of the run's picks.
 *
 * Censoring caveat: past picks were capped by SL supply, so in any
 * slot that filled the value understates demand and is only a
 * floor. The slots in {@link SECTION_SIGNUP_V2_DEFAULT_CAPPED_SLOTS}
 * are the ones capped by default; see its comment for which of them
 * the value is trusted for.
 */

/** Mean historical share of student picks, by `timeIndex`. */
export const SECTION_SIGNUP_V2_MEAN_SHARE_BY_SLOT: Readonly<
  Record<number, number>
> = Object.freeze({
  0: 0.0412,
  1: 0.0226,
  2: 0.0345,
  3: 0.0269,
  4: 0.0313,
  5: 0.0233,
  6: 0.0173,
  7: 0.0138,
  8: 0.0111,
  9: 0.0074,
  10: 0.0106,
  11: 0.0088,
  12: 0.0123,
  13: 0.0075,
  14: 0.0175,
  15: 0.0237,
  16: 0.0269,
  17: 0.0334,
  18: 0.034,
  19: 0.0332,
  20: 0.0324,
  21: 0.0225,
  22: 0.0241,
  23: 0.0153,
  24: 0.0182,
  25: 0.0202,
  26: 0.0221,
  27: 0.0244,
  28: 0.0211,
  29: 0.0157,
  30: 0.0112,
  31: 0.0152,
  32: 0.0092,
  33: 0.0081,
  34: 0.0091,
  35: 0.009,
  36: 0.012,
  37: 0.017,
  38: 0.0146,
  39: 0.0259,
  40: 0.0281,
  41: 0.0383,
  42: 0.0387,
  43: 0.0326,
  44: 0.0309,
  45: 0.0467,
});

/**
 * Slots whose SL sections are capped by default: the historically
 * SL-heavy slots, where supply was plentiful and meanShare is trusted,
 * plus slots 0 and 1 by team decision. Slots 0 and 1 may have been
 * short of SLs in past runs, so their meanShare may understate demand
 * and the admin should review their caps before writing them.
 */
export const SECTION_SIGNUP_V2_DEFAULT_CAPPED_SLOTS: readonly number[] =
  Object.freeze([0, 1, 24, 26, 27, 42, 43, 44, 45]);

/**
 * Computes SL section caps as
 * `ceil(expectedStudents × meanShare(slot) × safety / minStudentsPerSection)`:
 * the section count that covers the slot's historical share at
 * `minStudentsPerSection` students each, rounded up, so the last
 * section may fall below `minStudentsPerSection`.
 *
 * @param options.expectedStudents - Expected student picks in the pool.
 * @param options.minStudentsPerSection - Smallest acceptable
 *   students per section; must be positive.
 * @param options.safety - Headroom multiplier.
 * @param options.slots - Slots to cap. When omitted, uses
 *   {@link SECTION_SIGNUP_V2_DEFAULT_CAPPED_SLOTS}.
 * @param options.meanShareBySlot - Shares by `timeIndex`. When omitted,
 *   uses {@link SECTION_SIGNUP_V2_MEAN_SHARE_BY_SLOT}.
 * @return Caps keyed by `String(timeIndex)`, the shape of
 *   `slSlotCaps{Main,Minor}` on the meta doc.
 * @throws {Error} When `minStudentsPerSection` is not positive or a
 *   slot has no entry in the share table.
 */
export function computeSlSlotCaps({
  expectedStudents,
  minStudentsPerSection,
  safety,
  slots = SECTION_SIGNUP_V2_DEFAULT_CAPPED_SLOTS,
  meanShareBySlot = SECTION_SIGNUP_V2_MEAN_SHARE_BY_SLOT,
}: {
  expectedStudents: number;
  minStudentsPerSection: number;
  safety: number;
  slots?: readonly number[];
  meanShareBySlot?: Readonly<Record<number, number>>;
}): Record<string, number> {
  if (minStudentsPerSection <= 0) {
    throw new Error('computeSlSlotCaps: minStudentsPerSection must be > 0');
  }
  const caps: Record<string, number> = {};
  for (const slot of slots) {
    if (!Object.hasOwn(meanShareBySlot, slot)) {
      throw new Error(
        `computeSlSlotCaps: no meanShare for slot ${String(slot)}`,
      );
    }
    const meanShare = meanShareBySlot[slot];
    const raw = (expectedStudents * meanShare * safety) / minStudentsPerSection;
    // Float products of exact integers can land a hair above them.
    caps[String(slot)] = Math.ceil(raw - 1e-9);
  }
  return caps;
}
